import { ComponentProps, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Box, ChevronDownIcon, HStack, Pressable, ScrollView, VStack, ChevronUpIcon, Divider, useTheme } from 'native-base';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { ScrollView as RNScrollView } from 'react-native';
import { ClockIcon, ListFilter, Sparkles } from 'lucide-react';

import { Text, Button, ZapIcon, ZapFastIcon } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { CleaningScheduleTypeEnum, CleaningTaskStatusEnum } from '@pimm/services/lib/sms-workforce/types';
import { formatTo } from '@app/utils/date-formatter';
import { CleaningPlan, CleaningTaskItem } from '../reducers';
import { CleaningTasksTableItem } from './cleaning-task-table-item';
import { PopoverBulkScheduleMenu } from './popover-bulk-schedule-menu';
import { PopoverResetMenu } from './popover-reset-menu';
import { ResetConfirmationAction } from './reset-confirmation-modal';
import { getScheduleColor } from '../utils';

export type EditModeType = 'normal' | 'bulk' | 'copy-last-week' | 'auto-assign';

type CleaningTasksEditableViewProps = Pick<ComponentProps<typeof CleaningTasksTableItem>, 'onOpenLinkDocument'> & {
  plan: CleaningPlan;
  categories: string[];
  filteredCategories: string[];
  filteredTasksByCategory: Record<string, CleaningTaskItem[]>;
  originalTasksByCategory: Record<string, CleaningTaskItem[]>;
  expandedCategories: Set<string>;
  hasActiveFilters: boolean;
  categoryRefs: RefObject<{ [key: string]: any }>;
  scrollToCategory?: string;
  onToggleCategory?: (category: string) => void;
  editMode?: EditModeType;
  selectedTaskIds?: Set<number>;
  onSelectionChange?: (taskIds: Set<number>) => void;
  onExitEditMode?: () => void;
  onAutoAssignAll?: () => void;
  isAutoAssignLoading?: boolean;
  isAutoAssignSubmitted?: boolean;
  onAutoScheduleAll?: () => void;
  isAutoScheduleSubmitted?: boolean;
  isAutoScheduleLoading?: boolean;
  scheduledDate?: Date;
  isScheduleTimeLoading?: boolean;
  isScheduleTimeDisabled?: boolean;
  /** DOP start hour sourced from dopBlocks[blockNumber=1].startTime. Defaults to 5. */
  dopStartHour?: number;
  scheduleMin?: Date;
  scheduleMax?: Date;
  onApplyBulkScheduleTime?: (dateTime: Date, taskIds: number[]) => void;
  hasAutoAssigned?: boolean;
  hasAutoScheduled?: boolean;
  onConfirmReset?: (action: ResetConfirmationAction) => void;
  onClearFilters?: () => void;
  /** When true, renders PopoverBulkScheduleMenu as a Modal to avoid z-index conflicts with an outer Modal */
  isInsideModal?: boolean;
};

const isEligibleForBulk = (task: CleaningTaskItem): boolean =>
  task.assignee?.status !== CleaningTaskStatusEnum.Complete &&
  task.assignee?.status !== CleaningTaskStatusEnum.NotApplicable &&
  !task.assignee?.employeeId;

const isEligibleForCLW = (task: CleaningTaskItem): boolean => isEligibleForBulk(task) && !task.assignee?.isAutoScheduled;

const isAutoScheduledGreyout = (task: CleaningTaskItem): boolean =>
  !!task.assignee?.isAutoScheduled &&
  task.assignee?.status !== CleaningTaskStatusEnum.Complete &&
  task.assignee?.status !== CleaningTaskStatusEnum.NotApplicable &&
  !task.assignee?.employeeId;

const isEligibleForAutoAssign = (task: CleaningTaskItem): boolean =>
  isEligibleForBulk(task) && !task.assignee?.isAutoAssigned && !!task.assignee?.dueDate;

export const CleaningTasksEditableView = ({
  plan,
  categories,
  filteredCategories,
  filteredTasksByCategory,
  originalTasksByCategory,
  expandedCategories,
  hasActiveFilters,
  categoryRefs,
  scrollToCategory,
  onToggleCategory,
  editMode = 'normal',
  selectedTaskIds = new Set(),
  onSelectionChange,
  onExitEditMode,
  onAutoAssignAll,
  isAutoAssignSubmitted = false,
  isAutoAssignLoading = false,
  onAutoScheduleAll,
  isAutoScheduleSubmitted = false,
  isAutoScheduleLoading = false,
  scheduledDate,
  isScheduleTimeLoading = false,
  isScheduleTimeDisabled = false,
  dopStartHour,
  scheduleMin,
  scheduleMax,
  onApplyBulkScheduleTime,
  hasAutoAssigned = false,
  hasAutoScheduled = false,
  onConfirmReset,
  isInsideModal = false,
  ...props
}: CleaningTasksEditableViewProps) => {
  const { translate } = useAppLocale();
  const { t } = useTranslation();
  const scrollViewRef = useRef<RNScrollView>(null);
  const bulkScheduleOpen = useBoolean();
  const resetMenuOpen = useBoolean();
  const wasScheduleLoadingRef = useRef(false);
  const wasAutoAssignLoadingRef = useRef(false);
  const wasAutoScheduleLoadingRef = useRef(false);
  const isBulkSubmitted = useBoolean();
  const isAutoScheduleLocalSubmitted = useBoolean();
  const isAutoAssignLocalSubmitted = useBoolean();
  const { colors } = useTheme();

  // Local selection mode — only exits on explicit button press or plan change.
  // Intentionally ignores parent resets (e.g. after auto-assign submit) so the
  // bar stays visible until the user clicks "Done" / "Exit".
  const [localEditMode, setLocalEditMode] = useState<EditModeType>(editMode);
  const prevPlanKeyRef = useRef(`${plan.scheduleType}|${plan.date}`);

  useEffect(() => {
    if (editMode !== 'normal') {
      setLocalEditMode(editMode);
    }
  }, [editMode]);

  useEffect(() => {
    const planKey = `${plan.scheduleType}|${plan.date}`;
    if (prevPlanKeyRef.current !== planKey) {
      prevPlanKeyRef.current = planKey;
      setLocalEditMode('normal');
      isAutoScheduleLocalSubmitted.setFalse();
      isAutoAssignLocalSubmitted.setFalse();
    }
  }, [plan.scheduleType, plan.date]);

  const handleExitEditMode = () => {
    // When inside a modal the parent closes it immediately, which unmounts this
    // component after the animation. Resetting local state first would cause
    // the edit-mode UI to flash away while the modal is still fading out.
    if (!isInsideModal) {
      setLocalEditMode('normal');
      if (isBulkSubmitted.value) isBulkSubmitted.setFalse();
      if (isAutoScheduleLocalSubmitted.value) isAutoScheduleLocalSubmitted.setFalse();
      if (isAutoAssignLocalSubmitted.value) isAutoAssignLocalSubmitted.setFalse();
    }
    onExitEditMode?.();
  };

  useEffect(() => {
    if (wasScheduleLoadingRef.current && !isScheduleTimeLoading) {
      bulkScheduleOpen.setFalse();
      isBulkSubmitted.setTrue();
      onSelectionChange?.(new Set());
    }
    wasScheduleLoadingRef.current = isScheduleTimeLoading;
  }, [isScheduleTimeLoading]);

  useEffect(() => {
    if (wasAutoAssignLoadingRef.current && !isAutoAssignLoading && isAutoAssignSubmitted) {
      onSelectionChange?.(new Set());
      isAutoAssignLocalSubmitted.setTrue();
    }
    wasAutoAssignLoadingRef.current = isAutoAssignLoading;
  }, [isAutoAssignLoading, isAutoAssignSubmitted]);

  useEffect(() => {
    if (wasAutoScheduleLoadingRef.current && !isAutoScheduleLoading && isAutoScheduleSubmitted) {
      onSelectionChange?.(new Set());
      isAutoScheduleLocalSubmitted.setTrue();
    }
    wasAutoScheduleLoadingRef.current = isAutoScheduleLoading;
  }, [isAutoScheduleLoading, isAutoScheduleSubmitted]);

  // Reset scroll to top on mount. When inside a modal, defer past the fadeIn
  // animation (~300ms) so the browser's auto-focus scroll doesn't undo the reset.
  useEffect(() => {
    const timer = setTimeout(() => scrollViewRef.current?.scrollTo({ y: 0, animated: false }), isInsideModal ? 350 : 0);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (scrollToCategory && categoryRefs.current?.[scrollToCategory] && scrollViewRef.current) {
      const categoryElement = categoryRefs.current[scrollToCategory];
      categoryElement.measureLayout(
        scrollViewRef.current,
        (_left: number, top: number) => {
          scrollViewRef.current?.scrollTo({ y: top, animated: true });
        },
        () => {
          categoryElement.measure?.((_x: number, _y: number, _width: number, _height: number, _pageX: number, pageY: number) => {
            scrollViewRef.current?.scrollTo({ y: Math.max(0, pageY - 100), animated: true });
          });
        },
      );
    }
  }, [scrollToCategory]);

  // ── Selection predicates ────────────────────────────────────────────────────
  const getSelectablePredicate = useCallback(
    (task: CleaningTaskItem) => {
      if (localEditMode === 'bulk') return isEligibleForBulk(task);
      if (localEditMode === 'copy-last-week') return isEligibleForCLW(task);
      if (localEditMode === 'auto-assign') return isEligibleForAutoAssign(task);
      return false;
    },
    [localEditMode],
  );

  const getVisiblePredicate = useCallback(
    (task: CleaningTaskItem) => {
      if (localEditMode === 'bulk') return isEligibleForBulk(task);
      if (localEditMode === 'copy-last-week') return isEligibleForBulk(task); // includes greyed-out auto-scheduled
      if (localEditMode === 'auto-assign') return isEligibleForAutoAssign(task);
      return true;
    },
    [localEditMode],
  );

  // ── Selection helpers ───────────────────────────────────────────────────────
  const handleToggleTaskSelection = (taskId: number) => {
    const newSet = new Set(selectedTaskIds);
    if (newSet.has(taskId)) newSet.delete(taskId);
    else newSet.add(taskId);
    isBulkSubmitted.setFalse();
    isAutoScheduleLocalSubmitted.setFalse();
    isAutoAssignLocalSubmitted.setFalse();
    onSelectionChange?.(newSet);
  };

  const handleSelectAll = () => {
    const allEligible = filteredCategories.flatMap(cat => filteredTasksByCategory[cat] ?? []).filter(getSelectablePredicate);
    isBulkSubmitted.setFalse();
    isAutoScheduleLocalSubmitted.setFalse();
    isAutoAssignLocalSubmitted.setFalse();
    onSelectionChange?.(new Set(allEligible.map(t => t.id!)));
  };

  const handleClearSelection = () => {
    isBulkSubmitted.setFalse();
    isAutoScheduleLocalSubmitted.setFalse();
    isAutoAssignLocalSubmitted.setFalse();
    onSelectionChange?.(new Set());
  };

  const isInSelectionMode = localEditMode !== 'normal';
  const scheduleTypeColor = colors[getScheduleColor(plan.scheduleType)];

  const exitLabel =
    (localEditMode === 'copy-last-week' && isAutoScheduleLocalSubmitted.value) ||
    (localEditMode === 'auto-assign' && isAutoAssignLocalSubmitted.value) ||
    (localEditMode === 'bulk' && isBulkSubmitted.value)
      ? t('cleaning_task:done')
      : t('cleaning_task:exit');

  return (
    <ScrollView ref={scrollViewRef} flex={1} showsVerticalScrollIndicator stickyHeaderIndices={[0]}>
      <Box>
        {isInSelectionMode && (
          <HStack
            px={4}
            py={3}
            bg={scheduleTypeColor[50]}
            borderBottomWidth={2}
            borderColor={scheduleTypeColor[500]}
            alignItems="center"
            justifyContent="space-between"
          >
            <HStack space={2} alignItems="center">
              <Pressable onPress={handleSelectAll}>
                <Text size="md" fontWeight={500} color="gray.700">
                  {t('cleaning_task:select_all')}
                </Text>
              </Pressable>
              {selectedTaskIds.size > 0 && (
                <>
                  <Text size="md" color="gray.300" fontWeight={500}>
                    ·
                  </Text>
                  <Pressable onPress={handleClearSelection}>
                    <Text size="md" fontWeight={500} color="gray.700">
                      {t('cleaning_task:clear')}
                    </Text>
                  </Pressable>
                </>
              )}
            </HStack>

            <HStack space={3} alignItems="center">
              <Text size="md" fontWeight={500} color="gray.700">
                <Text size="md" fontWeight={600} color="gray.900">
                  {selectedTaskIds.size}
                </Text>{' '}
                {t('cleaning_task:tasks_selected')}
              </Text>
              <Divider height="22px" bg={scheduleTypeColor[500]} orientation="vertical" />

              {localEditMode === 'bulk' &&
                (isBulkSubmitted.value ? (
                  <Button
                    variant="unstyled"
                    size="md"
                    borderWidth={0}
                    py={3}
                    bg="gray.200"
                    isDisabled
                    startIcon={<Sparkles size={18} color={colors.gray[700]} />}
                    _text={{ color: 'gray.700', fontWeight: 600, fontSize: 'md' }}
                  >
                    {`${t('cleaning_task:scheduled')}!`}
                  </Button>
                ) : (
                  <PopoverBulkScheduleMenu
                    isOpen={bulkScheduleOpen.value}
                    onOpen={bulkScheduleOpen.setTrue}
                    onClose={bulkScheduleOpen.setFalse}
                    selectedCount={selectedTaskIds.size}
                    scheduleType={plan.scheduleType}
                    dopStartHour={dopStartHour}
                    isBulk
                    isLoading={isScheduleTimeLoading}
                    scheduledDate={scheduledDate}
                    onApply={dateTime => onApplyBulkScheduleTime?.(dateTime, Array.from(selectedTaskIds))}
                    min={scheduleMin}
                    max={scheduleMax}
                    asModal={isInsideModal}
                    trigger={triggerProps => (
                      <Button
                        {...triggerProps}
                        bg={selectedTaskIds.size > 0 ? scheduleTypeColor[500] : 'gray.200'}
                        variant="unstyled"
                        borderWidth={0}
                        py={3}
                        isDisabled={selectedTaskIds.size === 0}
                        onPress={() => selectedTaskIds.size > 0 && bulkScheduleOpen.setTrue()}
                        startIcon={<ClockIcon size="14px" color={selectedTaskIds.size > 0 ? 'white' : colors.gray[800]} />}
                        _text={{ color: selectedTaskIds.size > 0 ? 'white' : colors.gray[800], fontWeight: 600, fontSize: 'md' }}
                      >
                        {t('cleaning_task:set_time_apply')}
                      </Button>
                    )}
                  />
                ))}

              {localEditMode === 'copy-last-week' && (
                <Button
                  variant="unstyled"
                  size="md"
                  borderWidth={0}
                  py={3}
                  bg={selectedTaskIds.size > 0 && !isAutoScheduleLocalSubmitted.value ? scheduleTypeColor[500] : 'gray.200'}
                  isDisabled={selectedTaskIds.size === 0 || isAutoScheduleLocalSubmitted.value || isAutoScheduleLoading}
                  isLoading={isAutoScheduleLoading}
                  isLoadingText={t('cleaning_task:auto_schedule_selected')}
                  onPress={onAutoScheduleAll}
                  startIcon={
                    isAutoScheduleLocalSubmitted.value ? (
                      <Sparkles size={18} color={colors.gray[700]} />
                    ) : (
                      <ZapIcon size="14px" color={selectedTaskIds.size > 0 ? 'white' : 'gray.700'} />
                    )
                  }
                  _text={{
                    color: selectedTaskIds.size > 0 && !isAutoScheduleLocalSubmitted.value ? 'white' : 'gray.700',
                    fontWeight: 600,
                    fontSize: 'md',
                  }}
                >
                  {isAutoScheduleLocalSubmitted.value ? `${t('cleaning_task:scheduled')}!` : t('cleaning_task:auto_schedule_selected')}
                </Button>
              )}

              {localEditMode === 'auto-assign' && (
                <Button
                  variant="unstyled"
                  size="md"
                  borderWidth={0}
                  py={3}
                  bg={
                    selectedTaskIds.size > 0 && !isAutoAssignLocalSubmitted.value && !isAutoAssignLoading
                      ? scheduleTypeColor[500]
                      : 'gray.200'
                  }
                  isDisabled={selectedTaskIds.size === 0 || isAutoAssignLocalSubmitted.value || isAutoAssignLoading}
                  isLoading={isAutoAssignLoading}
                  isLoadingText={t('cleaning_task:assigning')}
                  onPress={onAutoAssignAll}
                  startIcon={
                    isAutoAssignLocalSubmitted.value ? undefined : (
                      <ZapFastIcon size="md" color={selectedTaskIds.size > 0 ? 'white' : 'gray.400'} />
                    )
                  }
                  _text={{
                    color: selectedTaskIds.size > 0 && !isAutoAssignLocalSubmitted.value && !isAutoAssignLoading ? 'white' : 'gray.400',
                    fontWeight: 600,
                    fontSize: 'md',
                  }}
                >
                  {isAutoAssignLocalSubmitted.value ? `${t('cleaning_task:assigned')}!` : t('cleaning_task:auto_assign_selected')}
                </Button>
              )}

              <Button
                variant="unstyled"
                size="md"
                onPress={handleExitEditMode}
                _text={{ color: 'gray.700', fontWeight: 500, fontSize: 'md' }}
                borderWidth={0}
              >
                {exitLabel}
              </Button>
            </HStack>
          </HStack>
        )}

        {!isInSelectionMode && onConfirmReset && (
          <HStack px={4} py={2} justifyContent="flex-end" borderBottomWidth={1} borderColor="gray.100">
            <PopoverResetMenu
              isOpen={resetMenuOpen.value}
              onOpen={resetMenuOpen.setTrue}
              onClose={resetMenuOpen.setFalse}
              hasAutoAssigned={hasAutoAssigned}
              hasAutoScheduled={hasAutoScheduled}
              onConfirmReset={onConfirmReset}
            />
          </HStack>
        )}
      </Box>

      <VStack space={4} p={4} justifyContent="center" flex={hasActiveFilters && !filteredCategories.length ? 1 : undefined}>
        {hasActiveFilters && !filteredCategories.length && (
          <Box alignItems="center" justifyContent="center" flex={1}>
            <Text size="md" color="gray.500" fontWeight={500}>
              {t('cleaning_task:no_tasks_match_filters')}
            </Text>
          </Box>
        )}

        {filteredCategories.length > 0 && (
          <VStack space={4}>
            {filteredCategories.map(category => {
              const tasks = filteredTasksByCategory[category] ?? [];
              const originalTasks = originalTasksByCategory[category] ?? [];
              const isExpanded = expandedCategories.has(category);

              const nonNATasks = originalTasks.filter(task => task.assignee?.status !== CleaningTaskStatusEnum.NotApplicable);
              const categoryAssignedCount = nonNATasks.filter(task => !!task.assignee?.employeeId).length;
              const categoryTotal = nonNATasks.length;
              const categoryAllDone = categoryTotal > 0 && categoryAssignedCount === categoryTotal;
              const categoryPartial = categoryAssignedCount > 0 && categoryAssignedCount < categoryTotal;

              const selectableTasks = isInSelectionMode ? tasks.filter(getSelectablePredicate) : [];
              const selectedInCategory = selectableTasks.filter(t => selectedTaskIds.has(t.id!)).length;
              const allSelectedInCategory = selectableTasks.length > 0 && selectedInCategory === selectableTasks.length;

              const visibleTasks = isInSelectionMode ? tasks.filter(getVisiblePredicate) : tasks;

              if (isInSelectionMode && visibleTasks.length === 0) return null;

              const handleCategorySelectAll = () => {
                const newSet = new Set(selectedTaskIds);
                if (allSelectedInCategory) selectableTasks.forEach(t => newSet.delete(t.id!));
                else selectableTasks.forEach(t => newSet.add(t.id!));
                isBulkSubmitted.setFalse();
                isAutoScheduleLocalSubmitted.setFalse();
                isAutoAssignLocalSubmitted.setFalse();
                onSelectionChange?.(newSet);
              };

              return (
                <VStack
                  key={category}
                  space={0}
                  ref={ref => {
                    if (ref && categoryRefs.current) categoryRefs.current[category] = ref;
                  }}
                  style={{
                    shadowColor: 'black',
                    shadowOffset: { width: 0, height: 1 },
                    shadowOpacity: 0.06,
                    shadowRadius: 4,
                    elevation: 2,
                  }}
                  borderRadius="lg"
                >
                  <Pressable onPress={() => onToggleCategory?.(category)}>
                    <HStack
                      h={12}
                      alignItems="center"
                      px={4}
                      bg="white"
                      borderRadius={isExpanded ? undefined : 'lg'}
                      borderTopRadius="lg"
                      borderWidth={1}
                      borderBottomWidth={isExpanded ? 0 : 1}
                      borderColor="gray.200"
                    >
                      {isInSelectionMode && selectableTasks.length > 0 && (
                        <Pressable
                          onPress={e => {
                            e.stopPropagation?.();
                            handleCategorySelectAll();
                          }}
                          mr={3}
                        >
                          <Box
                            w={5}
                            h={5}
                            borderWidth={1.5}
                            borderColor={allSelectedInCategory || selectedInCategory > 0 ? scheduleTypeColor[400] : 'gray.300'}
                            bg={allSelectedInCategory || selectedInCategory > 0 ? scheduleTypeColor[400] : 'white'}
                            borderRadius="sm"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {allSelectedInCategory && (
                              <Box
                                w={2.5}
                                h={1.5}
                                borderBottomWidth={2}
                                borderLeftWidth={2}
                                borderColor="white"
                                style={{ transform: [{ rotate: '-45deg' }], marginTop: -2 }}
                              />
                            )}
                            {!allSelectedInCategory && selectedInCategory > 0 && <Box w={2} h={0.5} bg="white" />}
                          </Box>
                        </Pressable>
                      )}

                      <Text size="lg" fontWeight={600} color="black">
                        {translate(category, tasks[0]?.categoryTranslations)}
                      </Text>

                      <Box
                        ml="auto"
                        px={2}
                        py={0.5}
                        rounded="full"
                        borderWidth={1}
                        borderColor={
                          isInSelectionMode
                            ? selectedInCategory > 0
                              ? scheduleTypeColor[300]
                              : 'gray.300'
                            : categoryAllDone
                            ? `${getScheduleColor(plan.scheduleType)}.500`
                            : categoryPartial
                            ? scheduleTypeColor[200]
                            : 'gray.300'
                        }
                        bg={
                          isInSelectionMode
                            ? selectedInCategory > 0
                              ? scheduleTypeColor[50]
                              : 'gray.100'
                            : categoryAllDone
                            ? `${getScheduleColor(plan.scheduleType)}.500`
                            : categoryPartial
                            ? scheduleTypeColor[50]
                            : 'gray.100'
                        }
                      >
                        <Text
                          size="sm"
                          fontWeight={600}
                          color={
                            isInSelectionMode
                              ? selectedInCategory > 0
                                ? scheduleTypeColor[700]
                                : 'gray.400'
                              : categoryAllDone
                              ? 'white'
                              : categoryPartial
                              ? scheduleTypeColor[700]
                              : 'gray.400'
                          }
                          textTransform="lowercase"
                        >
                          {isInSelectionMode ? (
                            <>
                              {selectedInCategory}/{selectableTasks.length} {t('cleaning_task:selected')}
                            </>
                          ) : (
                            <>
                              {categoryAssignedCount}/{categoryTotal} {t('cleaning_task:assigned')}
                            </>
                          )}
                        </Text>
                      </Box>

                      <Box ml={2}>
                        {isExpanded ? <ChevronUpIcon size="4" color="gray.500" /> : <ChevronDownIcon size="4" color="gray.500" />}
                      </Box>
                    </HStack>
                  </Pressable>

                  {isExpanded && (
                    <Box borderRadius="lg" borderTopRadius={0} borderWidth={1} borderTopWidth={0} borderColor="gray.200" overflow="hidden">
                      <HStack
                        h={9}
                        bg={isInSelectionMode ? scheduleTypeColor[50] : 'gray.25'}
                        borderBottomWidth={1}
                        borderColor={isInSelectionMode ? scheduleTypeColor[200] : 'gray.200'}
                      >
                        {isInSelectionMode && <Box w="36px" />}
                        <Box justifyContent="center" pl={5} flex={1} h="full">
                          <Text size="sm" fontWeight={500} color="gray.700">
                            {t('cleaning_task:task')}
                          </Text>
                        </Box>
                        <Box justifyContent="center" w="180px" h="full">
                          <Text size="sm" fontWeight={500} color="gray.700">
                            {t('cleaning_task:due_by')}
                          </Text>
                        </Box>
                        {!isInSelectionMode && (
                          <>
                            <Box justifyContent="center" px={2} w="22%" maxWidth="220px" h="full">
                              <Text size="sm" fontWeight={500} color="gray.700">
                                {t('cleaning_task:assigned_to')}
                              </Text>
                            </Box>
                            <Box justifyContent="center" w="70px" h="full">
                              <Text size="sm" fontWeight={500} color="gray.700" textAlign="center">
                                {t('cleaning_task:n_a')}
                              </Text>
                            </Box>
                          </>
                        )}
                      </HStack>

                      {visibleTasks.length === 0 ? (
                        <Box alignItems="center" justifyContent="center" minH="60px">
                          <Text size="md" color="gray.500" fontWeight={500}>
                            {t('cleaning_task:no_available_task')}
                          </Text>
                        </Box>
                      ) : (
                        visibleTasks.map((task, index) => {
                          if (!isInSelectionMode) {
                            return (
                              <CleaningTasksTableItem
                                key={task.id}
                                isEditable
                                scheduleDate={plan.date}
                                scheduleType={plan.scheduleType}
                                task={task}
                                dopStartHour={dopStartHour}
                                onOpenLinkDocument={props.onOpenLinkDocument}
                              />
                            );
                          }

                          const isSelectable = getSelectablePredicate(task);
                          const isSelected = selectedTaskIds.has(task.id!);
                          const isGreyedOut = localEditMode === 'copy-last-week' && isAutoScheduledGreyout(task);

                          return (
                            <Pressable
                              key={task.id}
                              onPress={() => isSelectable && handleToggleTaskSelection(task.id!)}
                              disabled={!isSelectable}
                            >
                              <HStack
                                px={4}
                                py={2.5}
                                borderBottomWidth={index < visibleTasks.length - 1 ? 1 : 0}
                                borderColor="gray.100"
                                bg={isSelected ? scheduleTypeColor[50] : 'gray.50'}
                                opacity={isGreyedOut ? 0.45 : 1}
                                alignItems="center"
                              >
                                <Box w="36px" alignItems="center">
                                  {isGreyedOut ? (
                                    <Sparkles size={14} color={scheduleTypeColor[500]} />
                                  ) : (
                                    <Box
                                      w={4}
                                      h={4}
                                      borderWidth={1.5}
                                      borderColor={isSelected ? scheduleTypeColor[400] : 'gray.300'}
                                      bg={isSelected ? scheduleTypeColor[400] : 'white'}
                                      borderRadius="sm"
                                      alignItems="center"
                                      justifyContent="center"
                                    >
                                      {isSelected && (
                                        <Box
                                          w={2}
                                          h={1}
                                          borderBottomWidth={2}
                                          borderLeftWidth={2}
                                          borderColor="white"
                                          style={{ transform: [{ rotate: '-45deg' }], marginTop: -1 }}
                                        />
                                      )}
                                    </Box>
                                  )}
                                </Box>

                                <VStack flex={1}>
                                  <Text size="md" fontWeight={600} color={isGreyedOut ? 'gray.400' : 'gray.900'}>
                                    {translate(task.title, task.translations)}
                                  </Text>
                                  <Text size="sm" color="gray.500">
                                    {translate(task.positionTitle, task.positionTitleTranslations)}
                                  </Text>
                                </VStack>

                                <Box w="180px">
                                  {isGreyedOut ? (
                                    <HStack
                                      p={2}
                                      space={1}
                                      bg={scheduleTypeColor[50]}
                                      borderWidth={1}
                                      borderColor={scheduleTypeColor[200]}
                                      borderRadius="md"
                                      alignItems="center"
                                    >
                                      <Text size="sm" fontWeight={600} color={scheduleTypeColor[700]}>
                                        {t('cleaning_task:auto')}
                                      </Text>
                                      {task.assignee?.dueDate && (
                                        <Text size="sm" color="gray.500">
                                          {formatTo(task.assignee.dueDate, 'MMM DD h:mm A')}
                                        </Text>
                                      )}
                                    </HStack>
                                  ) : (
                                    <HStack p={2} space={1} borderWidth={1} borderColor="gray.200" borderRadius="md" alignItems="center">
                                      {task.assignee?.dueDate && (
                                        <ClockIcon size="12px" color={isSelected ? scheduleTypeColor[700] : colors.gray[800]} />
                                      )}
                                      {task.assignee?.dueDate ? (
                                        <Text
                                          size="sm"
                                          lineHeight="sm"
                                          fontWeight={500}
                                          color={isSelected ? scheduleTypeColor[700] : colors.gray[600]}
                                        >
                                          {formatTo(
                                            task.assignee.dueDate,
                                            plan.scheduleType === CleaningScheduleTypeEnum.Daily ? 'MMM DD h:mm A' : 'ddd, MMM DD h:mm A',
                                          )}
                                        </Text>
                                      ) : (
                                        <Text size="md" color="gray.500">
                                          {t(
                                            `cleaning_task:${
                                              plan.scheduleType === CleaningScheduleTypeEnum.Daily ? 'no_time_set' : 'no_date_and_time_set'
                                            }`,
                                          )}
                                        </Text>
                                      )}
                                      {task.assignee?.isAutoScheduled && <Sparkles size={12} color={scheduleTypeColor[500]} />}
                                    </HStack>
                                  )}
                                </Box>
                              </HStack>
                            </Pressable>
                          );
                        })
                      )}
                    </Box>
                  )}
                </VStack>
              );
            })}
          </VStack>
        )}

        {filteredCategories.length === 0 && !hasActiveFilters && (
          <VStack height="full" flex={1} alignItems="center" justifyContent="center" space={3}>
            <Box
              w={52}
              h={52}
              rounded="full"
              bg={colors.gray[50]}
              borderWidth={1}
              borderColor={colors.gray[200]}
              alignItems="center"
              justifyContent="center"
            >
              <ListFilter size={22} color={colors.gray[500]} />
            </Box>

            <VStack space={2} alignItems="center" justifyContent="center" minH="60px" width="full">
              <Text size="xl" color="gray.900" fontWeight={700}>
                {t('cleaning_task:no_tasks_match_filters')}
              </Text>
              <Text size="lg" color="gray.600" textAlign="center">
                {t('cleaning_task:no_tasks_match_filters_note')}
              </Text>
            </VStack>

            <Button
              flex={1}
              minH={44}
              onPress={props.onClearFilters}
              px={6}
              _text={{
                fontSize: 'lg',
                fontWeight: 600,
                lineHeight: 'md',
                color: 'gray.900',
              }}
            >
              {t('cleaning_task:clear_filters')}
            </Button>
          </VStack>
        )}
      </VStack>
    </ScrollView>
  );
};
